import { useIntl } from 'react-intl'
import styled from 'styled-components'
import { rem } from 'polished'

import ResponsiveImage from 'components/Common/ResponsiveImage'
import BodyText from 'LuxKit/Typography/BodyText'
import Heading from 'LuxKit/Typography/Heading'
import type { WishlistSummary } from '../types'

const Thumbnail = styled.div`
  width: ${rem(64)};
  height: ${rem(64)};
  overflow: hidden;
  border-radius: ${rem(16)};
  background: linear-gradient(135deg, #f1efe9 0%, #ddd8cf 100%);
  flex-shrink: 0;
`

const ThumbnailPlaceholder = styled.div`
  width: 100%;
  height: 100%;
  background: linear-gradient(135deg, #f1efe9 0%, #ddd8cf 100%);
`

const WishlistList = styled.div`
  display: grid;
`

const WishlistRow = styled.button`
  display: grid;
  grid-template-columns: min-content minmax(0, 1fr) min-content;
  gap: ${rem(16)};
  align-items: center;
  width: 100%;
  padding: ${rem(14)} 0;
  border: 0;
  color: inherit;
  text-align: start;
  background: transparent;
  cursor: pointer;

  & + & {
    border-top: ${rem(1)} solid rgba(44, 52, 64, 0.08);
  }

  &:hover {
    opacity: 0.88;
  }
`

const WishlistCopy = styled.div`
  min-width: 0;
  display: grid;
  gap: ${rem(4)};
`

const WishlistRowAction = styled.div`
  display: grid;
  place-items: center;
  width: ${rem(28)};
  height: ${rem(28)};
  border-radius: 50%;
  color: var(--color-normal-contrast);
  background: transparent;
  border: ${rem(1)} solid rgba(44, 52, 64, 0.18);
`

function WishlistThumbnail({ imageId, alt }: { imageId?: string; alt: string }) {
  return (
    <Thumbnail>
      {imageId ? (
        <ResponsiveImage id={imageId} alt={alt} aspectRatio="1:1" fit="center" objectFit="cover" />
      ) : (
        <ThumbnailPlaceholder />
      )}
    </Thumbnail>
  )
}

interface Props {
  wishlists: Array<WishlistSummary>
  isDisabled?: boolean
  onSelect: (wishlistId: string) => void
}

function WishlistPickerList({ wishlists, isDisabled = false, onSelect }: Props) {
  const intl = useIntl()

  return (
    <WishlistList>
      {wishlists.map((wishlist) => (
        <WishlistRow
          key={wishlist.id}
          type="button"
          aria-label={intl.formatMessage(
            {
              id: 'rcjna7R-tNycp4WYMMvJ',
              defaultMessage: 'Save to {wishlistName}',
              description: 'Aria label to add an offer to a specific wishlist in the save modal',
            },
            { wishlistName: wishlist.name },
          )}
          onClick={() => onSelect(wishlist.id)}
          disabled={isDisabled}
        >
          <WishlistThumbnail imageId={wishlist.imageId} alt={wishlist.name} />
          <WishlistCopy>
            <Heading variant="heading6">{wishlist.name}</Heading>
            <BodyText variant="medium" colour="light-foreground-alt">
              {intl.formatMessage(
                {
                  id: '0Uz-FOuubAGBEsh-iTEX',
                  defaultMessage: '{count, plural, one {# saved} other {# saved}}',
                  description: 'Saved offer count shown on wishlist rows in the picker modal',
                },
                { count: wishlist.itemCount },
              )}
            </BodyText>
          </WishlistCopy>
          <WishlistRowAction aria-hidden />
        </WishlistRow>
      ))}
    </WishlistList>
  )
}

export default WishlistPickerList
